import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { useData } from '../context/DataContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Search, MapPin, Building2, ShieldCheck, Star, Users, Filter, X, ArrowRight, Briefcase,
} from 'lucide-react';

const cityFromVendor = (v) => {
  const c = (v.city || '').split(',')[0].trim();
  return c || '—';
};

const VendorCard = ({ v }) => (
  <Link
    to={`/vendor/${v.slug || v.id}`}
    className="group bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-gray-800 rounded-lg p-4 flex flex-col gap-3 hover:shadow-lg hover:border-[#ee5e23] transition-all"
    data-testid={`directory-vendor-${v.id}`}
  >
    <div className="flex items-start gap-3">
      {v.logo_url ? (
        <img src={v.logo_url} alt={v.name} className="w-14 h-14 rounded-md object-cover border border-gray-200 shrink-0" />
      ) : (
        <div
          className="w-14 h-14 rounded-md flex items-center justify-center text-white font-bold text-lg shrink-0"
          style={{ background: v.plan_color || '#2c54b2' }}
        >
          {v.logo || (v.name || '?')[0]}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-gray-900 dark:text-gray-50 truncate group-hover:text-[#ee5e23] transition-colors">
          {v.name}
        </div>
        <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
          <MapPin className="w-3 h-3" /> {v.city || '—'}
        </div>
        {v.plan_name && (
          <span
            className="inline-block mt-1.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white"
            style={{ background: v.plan_color || '#2c54b2' }}
          >
            {v.plan_name}
          </span>
        )}
      </div>
    </div>

    {Array.isArray(v.categories) && v.categories.length > 0 && (
      <div className="flex flex-wrap gap-1">
        {v.categories.slice(0, 3).map((c) => (
          <span key={c} className="text-[11px] bg-orange-50 dark:bg-orange-900/20 text-[#c34a14] dark:text-orange-300 px-2 py-0.5 rounded-full">
            {c}
          </span>
        ))}
        {v.categories.length > 3 && (
          <span className="text-[11px] text-gray-500">+{v.categories.length - 3}</span>
        )}
      </div>
    )}

    <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 pt-2 border-t border-gray-100 dark:border-gray-800">
      <span className="inline-flex items-center gap-1">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> {v.gst || 'GST Pending'}
      </span>
      {v.rating ? (
        <span className="inline-flex items-center gap-1">
          <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" /> {v.rating}
        </span>
      ) : null}
    </div>

    <div className="flex items-center justify-between text-xs text-gray-500">
      <span className="inline-flex items-center gap-1">
        <Briefcase className="w-3.5 h-3.5" /> Since {v.established || '—'}
      </span>
      {v.employees && (
        <span className="inline-flex items-center gap-1">
          <Users className="w-3.5 h-3.5" /> {v.employees}
        </span>
      )}
    </div>

    <span className="text-xs font-semibold text-[#ee5e23] inline-flex items-center gap-1 mt-auto">
      View Profile <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
    </span>
  </Link>
);

const BusinessDirectoryPage = () => {
  const { tree } = useData();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [city, setCity] = useState('');
  const [mainCat, setMainCat] = useState('');
  const [sortBy, setSortBy] = useState('plan');

  useEffect(() => {
    document.title = 'Business Directory — TelanganaMart';
    setLoading(true);
    api.getSuppliers({ limit: 500 })
      .catch(() => [])
      .then((v) => setVendors(Array.isArray(v) ? v : []))
      .finally(() => setLoading(false));
  }, []);

  const cities = useMemo(() => {
    const set = new Set();
    vendors.forEach((v) => {
      const c = cityFromVendor(v);
      if (c && c !== '—') set.add(c);
    });
    return Array.from(set).sort();
  }, [vendors]);

  const RANDOM_PREVIEW_SIZE = 15;
  // Stable random subset of vendors — recomputed only when the underlying vendor
  // list changes (Fisher-Yates shuffle of indexes). Prevents reshuffling on every keystroke.
  const randomPreview = useMemo(() => {
    if (!vendors.length) return [];
    const idx = vendors.map((_, i) => i);
    for (let i = idx.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [idx[i], idx[j]] = [idx[j], idx[i]];
    }
    return idx.slice(0, RANDOM_PREVIEW_SIZE).map((i) => vendors[i]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendors.length]);

  const filtered = useMemo(() => {
    const qLower = q.trim().toLowerCase();
    const mainCatNames = mainCat
      ? new Set(
          (tree || [])
            .filter((m) => m.slug === mainCat || m.id === mainCat)
            .flatMap((m) => (m.categories || []).map((c) => (c.name || '').toLowerCase()))
        )
      : null;

    let arr = vendors.filter((v) => {
      if (qLower) {
        const hay = `${v.name} ${v.city} ${(v.categories || []).join(' ')} ${v.business_type || ''}`.toLowerCase();
        if (!hay.includes(qLower)) return false;
      }
      if (city && cityFromVendor(v).toLowerCase() !== city.toLowerCase()) return false;
      if (mainCatNames) {
        const has = (v.categories || []).some((c) => mainCatNames.has((c || '').toLowerCase()));
        if (!has) return false;
      }
      return true;
    });

    const planRank = (p) => ({ Platinum: 0, Gold: 1, Silver: 2 }[p] ?? 9);
    if (sortBy === 'plan') arr.sort((a, b) => planRank(a.plan_name) - planRank(b.plan_name) || (b.rating || 0) - (a.rating || 0));
    else if (sortBy === 'rating') arr.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    else if (sortBy === 'name') arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    else if (sortBy === 'newest') arr.sort((a, b) => (b.established || 0) - (a.established || 0));
    return arr;
  }, [vendors, q, city, mainCat, sortBy, tree]);

  const clearFilters = () => { setQ(''); setCity(''); setMainCat(''); };
  const hasFilters = q || city || mainCat;
  // Show 15 random vendors on the default landing — full filtered list once the
  // user searches or applies any filter.
  const displayed = hasFilters ? filtered : randomPreview;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0b1220]">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2c54b2] via-[#1f3f87] to-[#0e1e44] text-white">
        <div className="max-w-7xl mx-auto px-4 py-10">
          <div className="flex items-center gap-2 text-xs text-blue-100 mb-2">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <span className="text-white">Business Directory</span>
          </div>
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider bg-white/10 px-3 py-1 rounded-full mb-3">
                <Building2 className="w-3.5 h-3.5" /> Verified suppliers across Telangana
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
                Business Directory
              </h1>
              <p className="text-blue-100 mt-2 text-sm sm:text-base max-w-2xl">
                Browse {vendors.length}+ verified vendors. Filter by city, category and credentials to find the right supplier for your business.
              </p>
            </div>
            <Link to="/vendor/register">
              <Button className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="directory-list-business-btn">
                List Your Business
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Filter bar */}
      <section className="sticky top-[120px] z-20 bg-white dark:bg-[#0f172a] border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by company name, category, business type..."
              className="pl-8"
              data-testid="directory-search-input"
            />
          </div>
          <select
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="border rounded-md px-3 py-2 text-sm bg-white dark:bg-[#0f172a]"
            data-testid="directory-city-select"
          >
            <option value="">All Cities</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={mainCat}
            onChange={(e) => setMainCat(e.target.value)}
            className="border rounded-md px-3 py-2 text-sm bg-white dark:bg-[#0f172a]"
            data-testid="directory-maincat-select"
          >
            <option value="">All Industries</option>
            {(tree || []).map((m) => <option key={m.id} value={m.slug}>{m.name}</option>)}
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border rounded-md px-3 py-2 text-sm bg-white dark:bg-[#0f172a]"
            data-testid="directory-sort-select"
          >
            <option value="plan">Sort: Top members first</option>
            <option value="rating">Sort: Highest rated</option>
            <option value="name">Sort: A → Z</option>
            <option value="newest">Sort: Newest businesses</option>
          </select>
          {hasFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters} data-testid="directory-clear-filters">
              <X className="w-3.5 h-3.5 mr-1" /> Clear
            </Button>
          )}
          <span className="ml-auto text-xs text-gray-500" data-testid="directory-count">
            <Filter className="w-3.5 h-3.5 inline mr-1" />
            {filtered.length} of {vendors.length} businesses
          </span>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={`skeleton-${i}`} className="bg-white border rounded-lg p-4 h-56 animate-pulse">
                <div className="w-14 h-14 bg-gray-200 rounded mb-3" />
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 && hasFilters ? (
          <div className="text-center py-20 bg-white dark:bg-[#0f172a] rounded-lg border border-dashed border-gray-300">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">No businesses match these filters</h3>
            <p className="text-sm text-gray-500 mt-1">Try clearing filters or searching with a different keyword.</p>
            {hasFilters && (
              <Button variant="outline" onClick={clearFilters} className="mt-4" data-testid="directory-empty-clear">
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <>
            {!hasFilters && vendors.length > RANDOM_PREVIEW_SIZE && (
              <div
                className="mb-4 flex items-center gap-2 px-3 py-2 rounded-md bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900 text-blue-900 dark:text-blue-100 text-xs"
                data-testid="directory-random-preview-banner"
              >
                <Search className="w-3.5 h-3.5 shrink-0" />
                <span>
                  Showing <b>{displayed.length} businesses</b> at random from <b>{vendors.length}</b> verified vendors.
                  Use the search above to find a specific business, city or industry.
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-grid">
              {displayed.map((v) => <VendorCard key={v.id} v={v} />)}
            </div>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
};

export default BusinessDirectoryPage;
